import type { CSSProperties, HTMLAttributes } from 'react'
import { cn } from './cn'

/**
 * SkeletonProps — backward-compatible SUPERSET of two historically-divergent
 * Skeleton APIs that both ship in production VJAL apps:
 *
 *  1. Canonical "Tailwind-class" form (the original @vjal/ui contract):
 *     `<Skeleton variant="rect|circle|text" width="w-32" height="h-4" lines={3} label="…" />`
 *     where `width`/`height` are Tailwind sizing CLASSES (e.g. `w-32`, `h-4`,
 *     `w-full`). Sizing is applied via className.
 *
 *  2. Fork "CSS-value" form (coaching-dashboard / consulting-dashboard):
 *     `<Skeleton shape="text|block|circle" width="60%" height="1rem" />` (also
 *     `width={120} height={16}` numeric → px) where `width`/`height` are raw CSS
 *     dimensions applied via inline `style`, plus arbitrary passthrough props.
 *
 * The component auto-detects which contract is in play from the value form of
 * `width`/`height` and the presence of `shape` (see `isCssDimension`). The two
 * forms are NOT mixed in any real call site, so detection is unambiguous.
 */
export interface SkeletonProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  /** Canonical shape variant — `rect` (default), `circle`, `text`. */
  variant?: 'rect' | 'circle' | 'text'
  /**
   * Fork shape — `text` (default), `block`, `circle`. Supplying `shape` selects
   * the CSS-value rendering path (inline `style` sizing).
   */
  shape?: 'text' | 'block' | 'circle'
  /**
   * Width. Tailwind class (`w-32`, `w-full`) for the canonical form, OR a raw
   * CSS dimension (`'60%'`, `'8rem'`, number → px) for the fork form.
   */
  width?: number | string
  /**
   * Height. Tailwind class (`h-4`) for the canonical form, OR a raw CSS
   * dimension (`'1rem'`, number → px) for the fork form.
   */
  height?: number | string
  /** Number of text lines (only used when variant="text"). Default 1. */
  lines?: number
  className?: string
  /** Accessible label — when set, renders role="status" + aria-label. Default: hidden from AT. */
  label?: string | null
}

/** Tailwind sizing classes look like `w-32`, `h-4`, `w-full`, `w-2/3`, `min-h-0`, `size-10`. */
const TAILWIND_SIZE_CLASS = /^(?:w|h|min-w|min-h|max-w|max-h|size)-/

/**
 * True when a width/height value is a raw CSS dimension (fork form) rather than
 * a Tailwind sizing class (canonical form). Numbers and any string that is not a
 * Tailwind sizing class (e.g. `'1rem'`, `'60%'`, `'16px'`) are CSS dimensions.
 */
function isCssDimension(value: number | string | undefined): boolean {
  if (value === undefined) return false
  if (typeof value === 'number') return true
  return !TAILWIND_SIZE_CLASS.test(value.trim())
}

/** Coerce a CSS dimension to a style value (number → px). */
const dim = (v: number | string | undefined): string | undefined =>
  typeof v === 'number' ? `${v}px` : v

/**
 * Skeleton — non-blocking loading placeholder using the brand surface token.
 *
 * Canonical (Tailwind-class) form:
 *   `<Skeleton variant="text" lines={3} />` for paragraph placeholders,
 *   `<Skeleton variant="circle" width="w-10" height="h-10" />` for avatars.
 *
 * Fork (CSS-value) form:
 *   `<Skeleton height="1rem" width="60%" />`,
 *   `<Skeleton height="1.75rem" width="40%" shape="block" />`,
 *   `<Skeleton width={120} height={16} />`.
 */
export function Skeleton({
  variant = 'rect',
  shape,
  width,
  height,
  lines = 1,
  className = '',
  label = null,
  style,
  ...rest
}: SkeletonProps) {
  // `vjal-skeleton` is a backward-compat marker class preserved from the pre-promotion
  // forked Skeleton so existing consumers/tests can select loading placeholders by class.
  // v2.1 shimmer sheen (motion-reduce falls back to pulse). Marker class stays for probes/tests.
  const base = 'vjal-skeleton bg-shimmer bg-[length:200%_100%] animate-shimmer motion-reduce:animate-pulse rounded'
  const a11y = label
    ? { role: 'status' as const, 'aria-label': label }
    : { 'aria-hidden': true as const }

  // The CSS-value (fork) path is selected when an explicit `shape` is given, or
  // when either dimension is a raw CSS dimension rather than a Tailwind class.
  const useCssForm = shape !== undefined || isCssDimension(width) || isCssDimension(height)

  if (useCssForm) {
    const resolvedShape = shape ?? 'text'
    const s: CSSProperties = {
      width: dim(width),
      height: resolvedShape === 'circle' ? dim(width ?? height) : dim(height ?? '1em'),
      ...style,
    }
    return (
      <span
        {...a11y}
        className={cn(
          'vjal-skeleton bg-shimmer bg-[length:200%_100%] animate-shimmer motion-reduce:animate-pulse block',
          resolvedShape === 'circle' ? 'rounded-full' : resolvedShape === 'block' ? 'rounded-control' : 'rounded',
          className
        )}
        style={s}
        {...rest}
      />
    )
  }

  // ── Canonical Tailwind-class form (unchanged behavior) ──
  const widthClass = (width as string | undefined) ?? 'w-full'
  const heightClass = height as string | undefined

  if (variant === 'text') {
    return (
      <span {...a11y} className={cn('inline-block', widthClass, className)} style={style} {...rest}>
        {Array.from({ length: Math.max(1, lines) }).map((_, lineNum) => (
          <span
            key={`skeleton-line-${lineNum}`}
            className={cn(
              base,
              'block h-3 my-1',
              lineNum === lines - 1 ? 'w-2/3' : 'w-full'
            )}
          />
        ))}
      </span>
    )
  }

  if (variant === 'circle') {
    const size = heightClass || widthClass
    return <span {...a11y} className={cn(base, 'rounded-full', size, className)} style={style} {...rest} />
  }

  return <span {...a11y} className={cn(base, widthClass, heightClass || 'h-4', className)} style={style} {...rest} />
}

export interface SkeletonTextProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  /** Number of lines. Default 3. */
  lines?: number
  /** Width of the final (short) line. Default '60%'. */
  lastLineWidth?: number | string
}

/**
 * SkeletonText — a stack of text-line skeletons for paragraph/card loading
 * placeholders. Last line is shortened. Decorative: aria-hidden.
 */
export function SkeletonText({ lines = 3, lastLineWidth = '60%', className = '', ...props }: SkeletonTextProps) {
  const dim = (v: number | string) => (typeof v === 'number' ? `${v}px` : v)
  return (
    <div className={cn('flex flex-col gap-2', className)} aria-hidden="true" {...props}>
      {Array.from({ length: Math.max(1, lines) }, (_, lineNo) => (
        <div
          key={`skel-text-${lineNo}`}
          className="vjal-skeleton bg-shimmer bg-[length:200%_100%] animate-shimmer motion-reduce:animate-pulse rounded h-3"
          style={{ width: dim(lineNo === lines - 1 ? lastLineWidth : '100%') }}
        />
      ))}
    </div>
  )
}

export interface SkeletonRowsProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  /** Number of rows. Default 5. */
  rows?: number
  /** Number of cells per row. Default 4. */
  columns?: number
}

/**
 * SkeletonRows — a table-shaped grid of skeletons for loading DataTables/lists.
 * Announces loading via role=status / aria-busy.
 */
export function SkeletonRows({ rows = 5, columns = 4, className = '', ...props }: SkeletonRowsProps) {
  return (
    <div className={cn('flex flex-col', className)} role="status" aria-busy="true" aria-live="polite" {...props}>
      <span className="sr-only">Loading…</span>
      {Array.from({ length: Math.max(1, rows) }, (_, r) => (
        <div key={`skel-row-${r}`} className="flex items-center gap-4 px-5 py-3 border-b border-line last:border-0">
          {Array.from({ length: Math.max(1, columns) }, (_, c) => (
            <div
              key={`skel-cell-${r}-${c}`}
              className={cn('vjal-skeleton bg-shimmer bg-[length:200%_100%] animate-shimmer motion-reduce:animate-pulse rounded h-3', c === 0 ? 'w-2/5' : 'flex-1')}
            />
          ))}
        </div>
      ))}
    </div>
  )
}
